import { useState, useEffect } from "react";
import { apiFetch } from "../pages/marketplace/api";

/**
 * Encapsulates Marketplace Home data loading + paging + search state.
 * Extracted from MarketplaceHome.jsx (Session 30, PD-2).
 *
 * Session 63: the grid is filtered by **shelf** (platform-owned aisles with artwork), not by
 * the vendor's own category word — that word is now their storefront Collection.
 *
 * @param {Object} opts
 * @param {number} [opts.perPage=15] — products per page
 */
export function useMarketplaceData({ perPage = 15 } = {}) {
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [shelves, setShelves] = useState([]);

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  // A shelf pill on another marketplace page links here with ?shelf=slug
  const [activeShelf, setActiveShelf] = useState(() => {
    if (typeof window === "undefined") return "all";
    return new URLSearchParams(window.location.search).get("shelf") || "all";
  });
  // Session 62d: sort and a price range — the grid only ever had category chips and a search box.
  // Default is the nightly shuffle, so page one is not permanently the newest uploads.
  const [sort, setSort] = useState("mix");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  const [loading, setLoading] = useState(true);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when the shelf or a filter changes
  useEffect(() => { setPage(1); }, [activeShelf, sort, minPrice, maxPrice]);

  // Initial data load: vendors, cart, shelves
  useEffect(() => {
    Promise.all([
      apiFetch("/marketplace/vendors").catch(() => []),
      apiFetch("/cart").catch(() => ({ items: [] })),
      apiFetch("/marketplace/shelves").catch(() => ({ shelves: [] })),
    ]).then(([v, c, s]) => {
      setVendors(v);
      setCartCount(c.items?.length || 0);
      setShelves(s.shelves || []);
    });
  }, []);

  // Fetch products whenever page/shelf/search changes
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: perPage });
    if (activeShelf && activeShelf !== "all") params.set("shelf", activeShelf);
    if (searchDebounced) params.set("search", searchDebounced);
    if (sort) params.set("sort", sort);
    if (minPrice !== "") params.set("min_price", minPrice);
    if (maxPrice !== "") params.set("max_price", maxPrice);

    apiFetch(`/marketplace/products?${params}`)
      .then((data) => {
        setProducts(
          (data.products || []).filter(
            (item) => item.type === "physical" || item.type === "digital"
          )
        );
        setTotalPages(data.total_pages || 1);
        setTotalProducts(data.total || 0);
      })
      .catch(() => {
        setProducts([]);
        setTotalPages(1);
        setTotalProducts(0);
      })
      .finally(() => setLoading(false));
  }, [page, activeShelf, searchDebounced, perPage, sort, minPrice, maxPrice]);

  /** Force a refresh of the cart count (e.g. after closing CartDrawer). */
  const refreshCartCount = () => {
    apiFetch("/cart")
      .catch(() => ({ items: [] }))
      .then((c) => setCartCount(c.items?.length || 0));
  };

  const activeShelfName =
    activeShelf === "all"
      ? null
      : (shelves.find((s) => s.slug === activeShelf) || {}).name || null;

  return {
    // data
    vendors,
    products,
    shelves,
    cartCount,
    // pagination + filter state
    page,
    totalPages,
    totalProducts,
    activeShelf,
    activeShelfName,
    search,
    searchDebounced,
    loading,
    sort,
    minPrice,
    maxPrice,
    // setters
    setSearch,
    setActiveShelf,
    setPage,
    setSort,
    setMinPrice,
    setMaxPrice,
    refreshCartCount,
  };
}
